/**
 * ESCHER FALLS — 日本語。**辞書の原型はここ。**
 *
 * 他の言語は `Strings`（＝この形）を満たすことを型で強制されるので、
 * **ここに1つ足すと、訳していない言語が全部型検査で落ちる。**
 * 訳し忘れを目で探さなくてよい代わりに、増やすときは10言語ぶん増える。
 */

// 鍵の型は**返す側**が持つ。ui が game に依るのは自然だが、逆は淀む。
import type { RankKey } from '../../game/progression';
import type { AudioStateKey } from '../../audio/engine';

/** 通水に失敗した理由。`ui/hud.ts` が組み立てる。 */
export type FailKey = 'time' | 'moves' | 'drywell' | 'water' | 'deadend';

export const JA = {
  // — 盤面
  pour: '放水',
  lever: 'レバー',
  leverAria: 'レバーを倒す',
  retry: 'やりなおす',
  again: 'もういちど',
  next: 'つぎへ',
  share: 'この一枚を送る',
  shareFailed: '送れませんでした',
  rotateLeft: '視点を左へ回す',
  rotateRight: '視点を右へ回す',
  tiltUp: '視点を上げる',
  tiltDown: '視点を下げる',
  topDown: '真上',
  water: (left: number, total: number) => `水量 ${left} / ${total}`,
  moves: (left: number, total: number) => `手数 ${left} / ${total}`,
  timeLeft: (time: string) => `残り ${time}`,
  drops: (n: number) => `雫 ${n}`,

  // — 結果
  resultClear: '通った',
  resultFail: '通らなかった',
  clearTitle: '通水',
  fail: {
    time: '時が尽きた',
    moves: '手が尽きた',
    drywell: '涸れ井戸へ流れ込んだ',
    water: '水が尽きた',
    deadend: '水は行き止まりへ落ちた',
  } as Record<FailKey, string>,
  extendByAd: '広告を見て 30 秒延ばす',
  extendByDrops: (cost: number) => `雫 ${cost} で 30 秒延ばす`,
  multiplyByAd: (mult: number) => `広告を見て 雫 ×${mult}`,

  // — ステージ選択
  stages: 'ステージ',
  settingsAria: '設定',
  menuAria: 'メニュー',
  back: 'もどる',
  loop1: '一周目',
  loop2: '二周目',
  loopNote: '二周目は手数と時間が締まる。記録は一周目と分けて残る。',
  shop: 'ショップ',
  groupMain: '本編',
  groupExtra1: 'エクストラ',
  groupExtra2: 'エクストラ 其の二',
  freeTrial: '試遊',
  notPurchased: (n: string, name: string) => `${n} ${name}（未購入）`,
  lockedStage: '未解放のステージ',
  extraLockedFree: '一「逆しまの滝」は無料で遊べる。残りは設定（≡）から購入すると開く',
  extraLocked: '設定（≡）から購入すると開く',
  extra2Locked: '11面目からは、1〜10面をすべて通水すると開く',
  extra2LockedUnowned: '設定（≡）から購入し、1〜10面をすべて通水すると11面目から開く',
  extra2Note: 'エクストラをすべて通水すると開く',
  progress: (cleared: number, total: number) => `本編 ${cleared} / ${total} 通水`,
  totalTime: (time: string) => `合計 ${time}`,
  totalNone: '合計 —',

  // — 設定
  settings: '設定',
  bgm: '場の音',
  se: '効果音',
  testSound: '音を試す',
  audioStatus: (state: string) => `音の状態: ${state}`,
  audioState: {
    locked: '未解錠（画面を一度触ると起きる）',
    running: '動作中',
    suspended: '停止中',
  } as Record<AudioStateKey, string>,
  demoNote: (n: number) => `ここまでの ${n} 面が体験版です。続きの全42面は App で`,
  demoGet: 'App Store で入手',
  // — 今日の一面（日替わり）
  dailyTitle: '今日の一面',
  dailyLoading: '読み込み中…',
  dailyOffline: '通信できないため、今日の一面は開けません',
  dailyPlay: '遊ぶ',
  dailyCleared: (time: string) => `通水済み ${time}`,
  archiveTitle: 'これまでの一面',
  upcomingTitle: '先の一面',
  archiveLocked: '直近3日ぶんは無料。それより前の面は、エクストラを買うとすべて開く',
  language: '言語',
  languageAuto: '端末に合わせる',
  palette: '配色',
  worn: '着用中',
  owned: '所持',
  buyDrops: '雫を買う',
  restore: '購入を復元',
  writeReview: 'このアプリを評価する',
  moreApps: 'つくった人の他の作品',
  restoreChecking: '確かめています…',
  restoreDone: (n: number) => `${n} 件を復元しました`,
  restoreNone: '復元するものはありません',
  restoreFailed: '復元できませんでした',
  chooseStage: 'ステージを選ぶ',
  resume: 'つづける',

  // — 祝いの画面
  prizeLabel: 'クリア報酬',
  awardLabel: '称号',
  toExtra: 'エクストラへ',
  toLoop2: '二周目へ',
  toStart: 'はじまりへ',
  allClearTitle: '踏破',
  allClearBody: (n: number) => `${n} の水路すべてに水が通った`,
  multiplyTimes: (mult: number) => `広告を見て ${mult} 倍`,
  noteRank: '称号は記録に残る。ステージ選択の見出しに出る。',
  noteLoop2: '二周目が開いた。手数と時間が締まる。',
  notePartOne: 'ここまでが第一部。水は、まだ昇っていない。',
  inviteTry: 'エクストラ 一 を試す',
  inviteOpen: 'エクストラ 第一集を開く',
  dateLine: (d: Date) => `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`,

  // — 称号
  completionTitle: '無窮を渡りし者',
  rank: {
    none: '—',
    sovereign: '水を統べる者',
    architect: '滝の設計者',
    mason: '石工',
    apprentice: '見習い',
    wayfarer: '旅の者',
  } as Record<RankKey, string>,

  // — 配色
  // 鍵は `game/skins.ts` の id。訳が無い id は元の名前を出す。
  skin: {
    hakushi: '白紙',
    ai: '藍',
    koke: '苔',
    shu: '朱',
    sumi: '墨',
  } as Record<string, string>,

  // — 課金
  product: {
    drops600: '雫 600',
    drops1600: '雫 1600',
    drops5000: '雫 5000',
    noads: '広告を消す',
    extras: 'エクストラ 第一集 21面（広告なし）',
  } as Record<string, string>,
};

/** 辞書の形。**各言語はこれを満たさないと型検査で落ちる。** */
export type Strings = typeof JA;
